import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';
import { useLanguage } from './LanguageContext';

const SiteTextsContext = createContext();

export function SiteTextsProvider({ children }) {
  const { language } = useLanguage();
  const [texts, setTexts] = useState({});
  const [loading, setLoading] = useState(true);

  const loadTexts = useCallback(async (lang) => {
    try {
      const response = await axios.get(`${API}/site-texts?lang=${lang}`);
      setTexts(response.data);
    } catch (error) {
      console.error('Failed to load site texts:', error);
      // Keep existing texts on error
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTexts(language);
  }, [language, loadTexts]);

  // Get a text value with fallback
  const getText = useCallback((key, fallback = '') => {
    return texts[key] || fallback;
  }, [texts]);

  // Get multiple texts at once
  const getTexts = useCallback((keys) => {
    const result = {};
    keys.forEach(key => {
      result[key] = texts[key] || '';
    });
    return result;
  }, [texts]);

  // Refresh texts (for after CMS updates)
  const refreshTexts = useCallback(() => {
    loadTexts(language);
  }, [language, loadTexts]);

  return (
    <SiteTextsContext.Provider value={{ 
      texts, 
      getText, 
      getTexts, 
      loading,
      refreshTexts
    }}>
      {children}
    </SiteTextsContext.Provider>
  );
}

export function useSiteTexts() {
  const context = useContext(SiteTextsContext);
  if (!context) {
    throw new Error('useSiteTexts must be used within a SiteTextsProvider');
  }
  return context;
}

export default SiteTextsContext;
